// The standard floating toolbar: theme toggle, print/PDF, copy-as-Markdown (round-trip).
// Chrome behaviours: draggable anywhere on screen (position persists), and collapsible
// into a dot via a hover-revealed control (hidden state persists).
import { getMode, setMode, type Mode } from './theme'
import { collectMarkdown } from './export'
import { ICON } from './icons'
import { safeGet, safeSet } from './util'

const CYCLE: Mode[] = ['auto', 'light', 'dark']
const MODE_LABEL: Record<Mode, string> = { auto: 'Auto (system)', light: 'Light', dark: 'Dark' }

const POS_KEY = 'pagelet-toolbar-pos'
const HIDE_KEY = 'pagelet-toolbar-hidden'
const DOT_ID = 'pagelet-toolbar-dot'
const DRAG_THRESHOLD = 5

// The PDF button has three states: 'print' (off-platform — plain window.print fallback),
// 'generate' (deep-link into the dashboard's PDF modal), and 'sealed' (a sealed PDF already
// exists — open its public seal page). versions.ts drives the platform states via /__pdf.
type PdfState = { kind: 'print' } | { kind: 'generate'; href: string } | { kind: 'sealed'; href: string }
let pdfState: PdfState = { kind: 'print' }

export function setPdfState(state: PdfState): void {
  pdfState = state
  const btn = document.querySelector<HTMLButtonElement>('#pagelet-toolbar [data-act="pdf"]')
  if (!btn) return
  if (state.kind === 'sealed') {
    btn.innerHTML = ICON.download
    btn.title = 'Download the sealed PDF'
    btn.setAttribute('aria-label', 'Download the sealed PDF')
  } else if (state.kind === 'generate') {
    btn.innerHTML = ICON.pdf
    btn.title = 'Export as PDF'
    btn.setAttribute('aria-label', 'Export as PDF')
  }
}

interface Pos {
  x: number
  y: number
}

function storedPos(): Pos | null {
  try {
    const raw = safeGet(POS_KEY)
    if (!raw) return null
    const p = JSON.parse(raw) as Pos
    return typeof p?.x === 'number' && typeof p?.y === 'number' ? p : null
  } catch {
    return null
  }
}

function clampPos(x: number, y: number, w: number, h: number): Pos {
  return {
    x: Math.min(Math.max(8, x), Math.max(8, window.innerWidth - w - 8)),
    y: Math.min(Math.max(8, y), Math.max(8, window.innerHeight - h - 8)),
  }
}

const reducedMotion = (): boolean => window.matchMedia('(prefers-reduced-motion: reduce)').matches

export function mountToolbar(onPng?: () => void): void {
  if (document.body.getAttribute('data-pagelet-toolbar') === 'off') return
  if (document.getElementById('pagelet-toolbar') || document.getElementById(DOT_ID)) return

  if (safeGet(HIDE_KEY) === '1') {
    mountDot(null, onPng)
    return
  }

  const bar = document.createElement('div')
  bar.id = 'pagelet-toolbar'
  bar.setAttribute('role', 'toolbar')
  bar.setAttribute('aria-label', 'Pagelet tools')
  bar.innerHTML = `
    <button class="pagelet-tb-hide" title="Hide toolbar" aria-label="Hide toolbar">${ICON.minus}</button>
    <button data-act="theme" title="Theme: ${MODE_LABEL[getMode()]}" aria-label="Toggle theme">${ICON[getMode()]}</button>
    <button data-act="pdf" title="Print / PDF" aria-label="Print or export as PDF">${ICON.pdf}</button>
    <button data-act="png" title="Save as PNG" aria-label="Save as PNG">${ICON.png}</button>
    <button data-act="copy" title="Copy as Markdown" aria-label="Copy as Markdown">${ICON.copy}</button>`

  bar.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest('button')
    if (!btn) return
    if (btn.classList.contains('pagelet-tb-hide')) {
      void collapse(bar, onPng)
      return
    }
    if (bar.hasAttribute('data-dragged')) {
      // A drag just ended — swallow the click so buttons don't fire mid-move.
      bar.removeAttribute('data-dragged')
      return
    }
    const act = btn.dataset.act
    if (act === 'theme') cycleTheme(bar)
    else if (act === 'pdf') {
      if (pdfState.kind === 'print') window.print()
      else window.open(pdfState.href, '_blank', 'noopener')
    } else if (act === 'png') onPng?.()
    else if (act === 'copy') void copyMarkdown(bar)
  })

  document.body.appendChild(bar)
  setPdfState(pdfState) // re-apply state if the bar was remounted (expand)

  // Restore a previously dragged position (clamped into the current viewport).
  const pos = storedPos()
  if (pos) {
    const r = bar.getBoundingClientRect()
    const p = clampPos(pos.x, pos.y, r.width, r.height)
    bar.style.left = `${p.x}px`
    bar.style.top = `${p.y}px`
    bar.style.right = 'auto'
    bar.style.bottom = 'auto'
  }

  makeDraggable(bar)
}

function makeDraggable(bar: HTMLElement): void {
  let startX = 0
  let startY = 0
  let baseX = 0
  let baseY = 0
  let dragging = false

  bar.addEventListener('pointerdown', (e) => {
    if ((e.target as HTMLElement).closest('button')) return
    const r = bar.getBoundingClientRect()
    startX = e.clientX
    startY = e.clientY
    baseX = r.left
    baseY = r.top
    dragging = false
    bar.setPointerCapture(e.pointerId)
    const onMove = (ev: PointerEvent): void => {
      const dx = ev.clientX - startX
      const dy = ev.clientY - startY
      if (!dragging && Math.hypot(dx, dy) < DRAG_THRESHOLD) return
      if (!dragging) {
        dragging = true
        bar.classList.add('pagelet-tb-dragging')
      }
      const p = clampPos(baseX + dx, baseY + dy, r.width, r.height)
      bar.style.left = `${p.x}px`
      bar.style.top = `${p.y}px`
      bar.style.right = 'auto'
      bar.style.bottom = 'auto'
    }
    const onUp = (ev: PointerEvent): void => {
      bar.removeEventListener('pointermove', onMove)
      bar.removeEventListener('pointerup', onUp)
      bar.removeEventListener('pointercancel', onUp)
      bar.classList.remove('pagelet-tb-dragging')
      if (dragging) {
        const r2 = bar.getBoundingClientRect()
        safeSet(POS_KEY, JSON.stringify({ x: r2.left, y: r2.top }))
        bar.setAttribute('data-dragged', '1')
        ev.stopPropagation()
      }
    }
    bar.addEventListener('pointermove', onMove)
    bar.addEventListener('pointerup', onUp)
    bar.addEventListener('pointercancel', onUp)
  })
}

/** Collapse the toolbar into the dot, animating toward its own bottom-right corner. */
async function collapse(bar: HTMLElement, onPng?: () => void): Promise<void> {
  safeSet(HIDE_KEY, '1')
  const r = bar.getBoundingClientRect()
  const dot = mountDot({ x: r.right, y: r.bottom }, onPng)
  if (!reducedMotion()) {
    try {
      await bar.animate(
        [
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(0.35)' },
        ],
        { duration: 180, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
      ).finished
    } catch {
      /* animation interrupted — just remove */
    }
  }
  bar.remove()
  if (!reducedMotion()) {
    dot.animate(
      [
        { opacity: 0, transform: 'scale(0.4)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: 220, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'backwards' },
    )
  }
}

/** The collapsed dot. anchor = the bottom-right corner it should sit on (viewport coords). */
function mountDot(anchor: Pos | null, onPng?: () => void): HTMLElement {
  document.getElementById(DOT_ID)?.remove()
  const dot = document.createElement('button')
  dot.id = DOT_ID
  dot.title = 'Show Pagelet tools'
  dot.setAttribute('aria-label', 'Show Pagelet tools')
  dot.innerHTML = '<span></span>'
  document.body.appendChild(dot)

  const size = dot.getBoundingClientRect().width || 12
  if (anchor) {
    dot.style.left = `${anchor.x - size - 8}px`
    dot.style.top = `${anchor.y - size - 8}px`
    dot.style.right = 'auto'
    dot.style.bottom = 'auto'
  } else {
    // Hidden state restored from storage — reuse the dragged bar position if we have one.
    const pos = storedPos()
    if (pos) {
      const p = clampPos(pos.x, pos.y, size, size)
      dot.style.left = `${p.x}px`
      dot.style.top = `${p.y}px`
      dot.style.right = 'auto'
      dot.style.bottom = 'auto'
    }
  }

  dot.addEventListener('click', () => void expand(dot, onPng))
  return dot
}

async function expand(dot: HTMLElement, onPng?: () => void): Promise<void> {
  safeSet(HIDE_KEY, '0')
  const r = dot.getBoundingClientRect()
  if (!reducedMotion()) {
    try {
      await dot.animate(
        [
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 0, transform: 'scale(0.3)' },
        ],
        { duration: 140, easing: 'cubic-bezier(.4,0,1,1)', fill: 'forwards' },
      ).finished
    } catch {
      /* interrupted */
    }
  }
  dot.remove()
  // The toolbar mounts at its stored/default position; if we have neither, anchor where
  // the dot was so it reappears exactly where the user left it.
  if (!storedPos()) {
    safeSet(POS_KEY, JSON.stringify({ x: r.right - 150, y: r.bottom - 40 }))
  }
  mountToolbar(onPng)
  const bar = document.getElementById('pagelet-toolbar')
  if (bar && !reducedMotion()) {
    bar.animate(
      [
        { opacity: 0, transform: 'scale(0.6)' },
        { opacity: 1, transform: 'scale(1)' },
      ],
      { duration: 240, easing: 'cubic-bezier(.34,1.56,.64,1)', fill: 'backwards' },
    )
  }
}

function cycleTheme(bar: HTMLElement): void {
  const next = CYCLE[(CYCLE.indexOf(getMode()) + 1) % CYCLE.length]!
  setMode(next)
  const btn = bar.querySelector<HTMLButtonElement>('[data-act="theme"]')
  if (btn) {
    btn.innerHTML = ICON[next]
    btn.title = `Theme: ${MODE_LABEL[next]}`
  }
}

async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // Permission denied / unfocused document / non-secure context — legacy path.
  }
  try {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none'
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand('copy')
    ta.remove()
    return ok
  } catch {
    return false
  }
}

async function copyMarkdown(bar: HTMLElement): Promise<void> {
  const btn = bar.querySelector<HTMLButtonElement>('[data-act="copy"]')
  if (!btn) return
  const ok = await writeClipboard(collectMarkdown())
  btn.innerHTML = ok ? ICON.check : ICON.alert
  btn.classList.toggle('pagelet-copied', ok)
  setTimeout(() => {
    btn.innerHTML = ICON.copy
    btn.classList.remove('pagelet-copied')
  }, 1200)
}
